import { parseStreamCursor } from 'tripPlanner/utils/streamCursor'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { userIsSpoofed } from 'selectors/accountSelectors'
import { useGetChat } from 'tripPlanner/hooks/api/chat'
import { useSpoofSendGuard } from 'tripPlanner/hooks/useSpoofSendGuard'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { ChatMessageAttachment } from 'tripPlanner/api'
import { createChat, fileToChatMessageAttachment, toChatAttachmentSummary } from 'tripPlanner/api'
import type {
  AssistantAccommodationOfferPageSubmitPayload,
  AssistantSurfaceConfig,
  AssistantSurfaceContextFor,
  AssistantSurfaceContextSnapshot,
  AssistantSurfaceId,
  AssistantOfferPageTurnAuthority,
  AssistantSubmitPayload,
  AssistantTripPlannerSubmitPayload,
  AssistantUserData,
} from 'tripPlanner/assistant/api/types'
import {
  mapAssistantSubmitPayloadToCreateMessageRequest,
} from 'tripPlanner/assistant/api/messages'
import {
  cancelMessage,
  isChatBusyResponse,
  isPayloadTooLargeResponse,
  isStreamingUnavailableResponse,
  submitMessage,
  subscribeChatStream,
} from 'tripPlanner/api/v3'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { isAbortError } from 'api/requestUtils'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { StreamMachineEvent } from './useStreamMachine'
import type { StreamHandlers } from './useStreamEventProcessor'
import { isNetworkError, getStreamErrorDisplayMessage } from 'tripPlanner/utils/networkError'
import { FailedStreamTerminalError, getStreamTerminalStatus } from './streamTerminal'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { buildAssistantSurfaceContextSnapshot } from 'tripPlanner/assistant/surfaceContext'
import {
  buildAssistantTurnPolicySnapshot,
  buildAssistantTurnPolicySnapshotFromScope,
  isAssistantTurnPolicySnapshot,
  type AssistantTurnPolicySnapshot,
} from 'tripPlanner/assistant/surfacePolicy'
import {
  trackStreamStart,
  trackStreamComplete,
  trackStreamError,
  trackStreamResumed,
  trackReconnect,
} from 'tripPlanner/utils/streamAnalytics'
import { getTextFromParts } from 'tripPlanner/utils/messageParts'
import { publishAssistantStreamSyncEvent } from './assistantStreamSync'
import type { SubmitMessageOptions } from './submitMessageOptions'
type UserData = AssistantUserData

interface SubmitStreamConfig {
  kind: 'submit'
  chatId: number
  content: string
  userMessageId: string
  assistantMessageId: string
  regenerateMessageId?: string
  attachments?: Array<ChatMessageAttachment>
  // Same-ID retry only. Replays the exact authority accepted for the original
  // (chatId, userMessageId, assistantMessageId) turn instead of resolving a new
  // one — the snapshot-authority protocol requires a same-ID retry to reuse the
  // originally accepted snapshotRef/degradedSurfaceIdentity, never a fresh one.
  // Must never be set on a regenerate config; executeStream ignores it if
  // regenerateMessageId is also present, as defense against that happening.
  pinnedTurnAuthority?: AssistantOfferPageTurnAuthority
}

interface ResumeStreamConfig {
  kind: 'resume'
  chatId: number
  userMessageId?: string
  assistantMessageId: string
  assistantTurnPolicySnapshot?: AssistantTurnPolicySnapshot
}

type StreamConfig = SubmitStreamConfig | ResumeStreamConfig

interface UseStreamConnectionBaseArgs {
  // Machine
  send: (event: StreamMachineEvent) => void
  // Message overlay
  setPendingUserMessage: (message: MessageItem | undefined) => void
  setStreamingMessage: (message: MessageItem | undefined) => void
  invalidateChat: (forChatId?: number) => Promise<{ skipped: boolean }>
  // Event processor
  createHandlers: (generation: number, assistantMessageId: string) => StreamHandlers
  resetBuffer: () => void
  // Scroll
  scrollToNewMessage: (messageId: string) => void
  recalculateSpacerAfterStream: () => void
  // External callbacks
  refetchChatList: () => Promise<unknown>
  startChatTitleGeneration: (chatId: number, message: string, surfaceContextSnapshot?: AssistantSurfaceContextSnapshot) => void
  updateChatLastActivityTimestamp: (chatId: number, iso: string) => void
  onStreamComplete?: (chatId: number) => void
  // Config
  chatId: number | undefined
  setChatId: (id: number | undefined) => void
  isLoggedIn: boolean
  hasCancellableGeneration: boolean
  orderIds?: Array<string>
  files: Array<File>
  setFiles: React.Dispatch<React.SetStateAction<Array<File>>>
  useNewHotelSearch?: boolean
  userData: UserData
  messages: Array<MessageItem>
  messagesRef: React.MutableRefObject<Array<MessageItem>>
  isStreamingRef: React.MutableRefObject<boolean>
  messagesReady: boolean
  remoteStreamStartVersion: number
  latestGenerationRef: React.MutableRefObject<number>
  // Streaming message ID ref (shared with event processor)
  streamingMessageIdRef: React.MutableRefObject<string | undefined>
}

type UseStreamConnectionArgs = UseStreamConnectionBaseArgs & AssistantSurfaceConfig

interface BuildSubmitPayloadConfig {
  chatId: number
  content: string
  userMessageId: string
  assistantMessageId: string
  regenerateMessageId?: string
  attachments?: Array<ChatMessageAttachment>
  userData: UserData
}

export interface UseStreamConnectionReturn {
  handleSubmit: (message: string, options?: SubmitMessageOptions) => Promise<void>
  handleRegenerateResponse: () => Promise<void>
  handleStopStreaming: () => Promise<boolean>
  handleRetry: (config: {
    chatId: number
    userMessageId: string
    assistantMessageId: string
    content: string
    pinnedTurnAuthority?: AssistantOfferPageTurnAuthority
  }) => Promise<void>
  resetStreamingState: () => void
}

function buildTripPlannerSubmitPayload({
  chatId,
  content,
  userMessageId,
  assistantMessageId,
  regenerateMessageId,
  attachments,
  userData,
}: BuildSubmitPayloadConfig): AssistantTripPlannerSubmitPayload {
  if (regenerateMessageId !== undefined) {
    return {
      chatId,
      content,
      userMessageId,
      regenerateMessageId,
      userData,
      surface: 'trip_planner',
    }
  }

  return {
    chatId,
    content,
    userMessageId,
    assistantMessageId,
    userData,
    surface: 'trip_planner',
    ...(attachments && attachments.length > 0 ? { files: attachments } : {}),
  }
}

function buildAccommodationOfferPageSubmitPayload({
  chatId,
  content,
  userMessageId,
  assistantMessageId,
  regenerateMessageId,
  attachments,
  userData,
  surfaceContext,
}: BuildSubmitPayloadConfig & {
  surfaceContext: AssistantSurfaceContextFor<'accommodation_offer_page'>
}): AssistantAccommodationOfferPageSubmitPayload {
  if (regenerateMessageId !== undefined) {
    return {
      chatId,
      content,
      userMessageId,
      regenerateMessageId,
      userData,
      surface: 'accommodation_offer_page',
      surfaceContext,
    }
  }

  return {
    chatId,
    content,
    userMessageId,
    assistantMessageId,
    userData,
    surface: 'accommodation_offer_page',
    surfaceContext,
    ...(attachments && attachments.length > 0 ? { files: attachments } : {}),
  }
}

function buildSnapshotAuthoritySubmitPayload(
  config: BuildSubmitPayloadConfig,
  surface: AssistantSurfaceId,
  authority: AssistantOfferPageTurnAuthority,
): AssistantSubmitPayload {
  const { chatId, content, userMessageId, assistantMessageId, regenerateMessageId, attachments, userData } = config
  const files = attachments && attachments.length ? { files: attachments } : {}
  const authorityFields = authority.snapshotRef ?
      { snapshotRef: authority.snapshotRef } :
      { degradedSurfaceIdentity: authority.degradedSurfaceIdentity }

  if (regenerateMessageId !== undefined) {
    return {
      chatId,
      content,
      userMessageId,
      regenerateMessageId,
      userData,
      surface,
      ...authorityFields,
    }
  }

  return {
    chatId,
    content,
    userMessageId,
    assistantMessageId,
    userData,
    surface,
    ...files,
    ...authorityFields,
  }
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useStreamConnection({
  send,
  setPendingUserMessage,
  setStreamingMessage,
  invalidateChat,
  createHandlers,
  resetBuffer,
  scrollToNewMessage,
  recalculateSpacerAfterStream,
  refetchChatList,
  startChatTitleGeneration,
  updateChatLastActivityTimestamp,
  onStreamComplete,
  chatId,
  setChatId,
  isLoggedIn,
  hasCancellableGeneration,
  orderIds,
  files,
  setFiles,
  userData,
  useNewHotelSearch,
  surface,
  buildSurfaceContext,
  resolveTurnAuthority,
  messages,
  messagesRef,
  isStreamingRef,
  messagesReady,
  remoteStreamStartVersion,
  latestGenerationRef,
  streamingMessageIdRef,
}: UseStreamConnectionArgs): UseStreamConnectionReturn {
  const intl = useIntl()
  const accessToken = useAppSelector(state => state.auth.accessToken)
  const isSpoofed = useAppSelector(userIsSpoofed)
  const { currentRegionCode } = useContext(GeoContext)
  const { data: chatData } = useGetChat(chatId, currentRegionCode)
  const hiddenFromCustomer = chatData?.hiddenFromCustomer ?? true
  const { confirmOrAbort } = useSpoofSendGuard({ isSpoofed, hiddenFromCustomer, chatId })
  const streamAbortControllerRef = useRef<AbortController | undefined>(undefined)
  const streamCloseRef = useRef<(() => void) | undefined>(undefined)
  const activeStreamRef = useRef<{ chatId: number; generation: number } | undefined>(undefined)
  const buildSurfaceContextRef = useRef(buildSurfaceContext)
  const resolveTurnAuthorityRef = useRef(resolveTurnAuthority)
  const currentStreamingGenerationRef = useRef<number>(0)

  // Page-aware surfaces can rebuild context from changing page state without
  // recreating stream callbacks that are owned by the machine boundary. The
  // full TripPlanner chat surface intentionally has no page context builder.
  buildSurfaceContextRef.current = buildSurfaceContext
  resolveTurnAuthorityRef.current = resolveTurnAuthority

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })
  const networkErrorMessage = intl.formatMessage({
    id: 'zru2DO-wO2v0Kg1nYQ15',
    defaultMessage: 'Unable to connect. Please check your internet connection and try again.',
    description: 'Error message when chat fails due to network connectivity issues',
  })

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      streamCloseRef.current?.()
      streamCloseRef.current = undefined
      if (streamAbortControllerRef.current) {
        streamAbortControllerRef.current.abort()
        streamAbortControllerRef.current = undefined
      }
    }
  }, [])

  // ── Shared stream execution ──

  const getSurfaceContextSnapshot = useCallback((): AssistantSurfaceContextSnapshot => {
    return buildAssistantSurfaceContextSnapshot({
      surface,
      buildSurfaceContext: buildSurfaceContextRef.current,
    })
  }, [surface])

  const executeStream = useCallback(async(
    config: StreamConfig,
    thisGeneration: number,
    handlers: StreamHandlers,
  ) => {
    const { chatId: streamChatId, assistantMessageId, userMessageId } = config
    const isResume = config.kind === 'resume'
    const regenerateMessageId = isResume ? undefined : config.regenerateMessageId
    const overlayCreatedAt = new Date().toISOString()

    // Abort controller and generation ownership are claimed synchronously,
    // before any await, so Stop can cancel this stream even while it is still
    // resolving turn authority. Previously nothing existed to abort during
    // that window, so Stop during an in-flight snapshot ensure had no effect
    // and the message still posted once the ensure call settled.
    streamCloseRef.current?.()
    streamCloseRef.current = undefined

    const abortController = new AbortController()
    streamAbortControllerRef.current?.abort()
    streamAbortControllerRef.current = abortController

    activeStreamRef.current = {
      chatId: streamChatId,
      generation: thisGeneration,
    }
    streamingMessageIdRef.current = assistantMessageId
    resetBuffer()

    let surfaceContextSnapshot: AssistantSurfaceContextSnapshot | undefined
    let turnAuthority: AssistantOfferPageTurnAuthority | undefined
    let assistantTurnPolicySnapshot: AssistantTurnPolicySnapshot | undefined
    let capturedError: unknown
    let cursor = '0'
    let submitStarted = isResume

    try {
      if (isResume) {
        assistantTurnPolicySnapshot = config.assistantTurnPolicySnapshot
      } else if ((surface === 'accommodation_offer_page' || surface === 'trip_planner') && resolveTurnAuthorityRef.current) {
        // Same-ID retry replays the exact authority accepted for this
        // (chatId, userMessageId, assistantMessageId) pair instead of
        // resolving a new one. Never honoured for a regenerate config
        // (regenerateMessageId set) — regeneration must always resolve fresh.
        const pinnedTurnAuthority = regenerateMessageId === undefined ? config.pinnedTurnAuthority : undefined
        const resolvedTurnAuthority = pinnedTurnAuthority ??
          await resolveTurnAuthorityRef.current(streamChatId, accessToken ?? undefined, abortController.signal)
        if (resolvedTurnAuthority?.snapshotRef || resolvedTurnAuthority?.degradedSurfaceIdentity) {
          turnAuthority = resolvedTurnAuthority
          const regenerationScopeKey = resolvedTurnAuthority.snapshotRef ?
            resolvedTurnAuthority.snapshotRef.surfaceContextScopeKey :
            resolvedTurnAuthority.degradedSurfaceIdentity.surfaceContextScopeKey
          assistantTurnPolicySnapshot = buildAssistantTurnPolicySnapshotFromScope(
            surface,
            regenerationScopeKey,
          )
        } else {
          // Invalid-registry invariant (zero or 2+ sources registered) or the resolver
          // otherwise produced neither field. The snapshot protocol fails closed here —
          // never fall back to rebuilding/submitting the legacy inline surfaceContext,
          // which would silently accept ad hoc context as authority. Falling through to
          // the finally block below still sends STREAM_END so the machine returns to
          // idle, exactly like a Stop-during-resolution abort; nothing is submitted.
          return
        }
      } else {
        surfaceContextSnapshot = getSurfaceContextSnapshot()
        assistantTurnPolicySnapshot = buildAssistantTurnPolicySnapshot(surfaceContextSnapshot)
      }

      if (abortController.signal.aborted) {
        // Stop fired while resolving turn authority. Never submit or open the
        // stream for an aborted turn — falling through to `finally` below
        // still sends STREAM_END so the machine returns to idle instead of
        // getting stuck in `stopping`, which has no timeout of its own.
        return
      }

      const buildStreamingMessage = ({
        parts,
        rawContent,
        isPlaceholder,
      }: {
        parts: MessageItem['parts']
        rawContent: string
        isPlaceholder: boolean
      }): MessageItem => ({
        id: assistantMessageId,
        parts,
        role: 'assistant',
        rawContent,
        messageType: 'message',
        createdAt: overlayCreatedAt,
        generationStartedAt: overlayCreatedAt,
        isFinalized: false,
        isPlaceholder,
        ...(assistantTurnPolicySnapshot ? { assistantTurnPolicySnapshot } : {}),
      })

      // Placeholder overlay — present in DOM for scroll but zero-height until content arrives.
      // Resume path skips this (overlay already created with PG content by the resume effect).
      if (!isResume) {
        setStreamingMessage(buildStreamingMessage({
          parts: [],
          rawContent: '',
          isPlaceholder: !regenerateMessageId,
        }))
      }
      let overlayUpgraded = isResume

      send({
        type: 'CONNECTED',
        chatId: streamChatId,
        userMessageId,
        assistantMessageId,
        abortController,
        turnAuthority,
      })
      if (isResume) {
        trackStreamResumed(streamChatId)
      } else {
        trackStreamStart(streamChatId)
      }

      if (!isResume) {
        if (!(surfaceContextSnapshot || turnAuthority)) {
          throw new Error('Assistant surface context snapshot is required for new streams')
        }

        let requestPayload: AssistantSubmitPayload
        const submitPayloadConfig: BuildSubmitPayloadConfig = {
          chatId: streamChatId,
          content: config.content,
          userMessageId: config.userMessageId,
          assistantMessageId,
          regenerateMessageId: config.regenerateMessageId,
          attachments: config.attachments,
          userData,
        }

        if (turnAuthority) {
          requestPayload = buildSnapshotAuthoritySubmitPayload(submitPayloadConfig, surface, turnAuthority)
        } else if (surfaceContextSnapshot) {
          switch (surfaceContextSnapshot.surface) {
            case 'trip_planner':
              requestPayload = buildTripPlannerSubmitPayload(submitPayloadConfig)
              break
            case 'accommodation_offer_page':
              requestPayload = buildAccommodationOfferPageSubmitPayload({
                ...submitPayloadConfig,
                surfaceContext: surfaceContextSnapshot.surfaceContext,
              })
              break
            default:
              requestPayload = assertUnreachable(surfaceContextSnapshot)
          }
        } else {
          throw new Error('Assistant turn authority is required for new streams')
        }

        const messageRequest = mapAssistantSubmitPayloadToCreateMessageRequest(requestPayload, useNewHotelSearch)
        turnAuthority?.claimSubmittedActivity?.()
        submitStarted = true
        const submitResult = await submitMessage(
          messageRequest,
          accessToken ?? undefined,
        ).catch(error => {
          // These explicit admission failures do not create a message. Other
          // failures may have committed; their same-ID retry keeps pinned authority.
          if (isChatBusyResponse(error) || isPayloadTooLargeResponse(error) || isStreamingUnavailableResponse(error)) {
            turnAuthority?.releaseUnsubmittedActivity?.()
          }
          throw error
        })
        cursor = submitResult.cursor
        publishAssistantStreamSyncEvent('chat_stream_started', streamChatId)
      }

      await new Promise<void>((resolve, reject) => {
        // fetchEventSource silently resolves its own internal promise on
        // AbortError — none of our callbacks fire. Without this listener
        // the wrapper promise hangs forever after Stop, leaving the machine
        // stuck in `stopping`.
        abortController.signal.addEventListener('abort', () => resolve(), { once: true })

        let reconnectAttempt = 0
        let streamIdentity = parseStreamCursor(cursor)

        const sseConnection = subscribeChatStream(
          streamChatId,
          cursor,
          accessToken ?? undefined,
          {
            onEvent: (event, data, id) => {
              if (thisGeneration !== latestGenerationRef.current || abortController.signal.aborted) return
              const incoming = id ? parseStreamCursor(id) : undefined
              if (incoming?.protocol === 'attempt_v1') {
                if (incoming.messageId !== assistantMessageId ||
                  (streamIdentity?.protocol === 'attempt_v1' && incoming.generationStreamId !== streamIdentity.generationStreamId)) {
                  sseConnection?.close()
                  reject(new Error('Stream attempt changed; refreshing conversation'))
                  return
                }
                streamIdentity = incoming
              } else if (incoming && streamIdentity?.protocol === 'attempt_v1') {
                sseConnection?.close()
                reject(new Error('Stream lost its attempt identity'))
                return
              }
              if (incoming && event !== 'catch_up') {
                send({ type: 'CURSOR_UPDATE', cursor: id })
              }

              if (event === 'terminal') {
                sseConnection?.close()
                streamCloseRef.current = undefined
                const terminalStatus = getStreamTerminalStatus(data)
                if (terminalStatus === 'FAILED') {
                  reject(new FailedStreamTerminalError())
                  return
                }
                resolve()
                return
              }

              if (event === 'heartbeat' || (event === 'message' && !data)) {
                send({ type: 'HEARTBEAT' })
                return
              }

              if (event === 'submitted') {
                if (overlayUpgraded) {
                  overlayUpgraded = false
                  setStreamingMessage(buildStreamingMessage({
                    parts: [],
                    rawContent: '',
                    isPlaceholder: true,
                  }))
                }
                return
              }

              if (event === 'error') {
                try {
                  const parsed: unknown = JSON.parse(data)
                  const reason = parsed && typeof parsed === 'object' && 'reason' in parsed && typeof parsed.reason === 'string'
                    ? parsed.reason
                    : 'unknown'
                  reject(new Error(`Stream error: ${reason}`))
                } catch {
                  reject(new Error(`Stream error: ${data}`))
                }
                return
              }

              if (event === 'catch_up') {
                try {
                  handlers.restoreCatchUp(data, id, streamChatId)
                  send({ type: 'CURSOR_UPDATE', cursor: id })
                  overlayUpgraded = true
                } catch(error) {
                  sseConnection?.close()
                  reject(error)
                }
                return
              }
              // First SSE payload may be reasoning/status with no visible reply yet.
              // Keep the zero-height placeholder until real parts/content arrive so the
              // loader does not jump away from the user bubble (TRP-4316).
              if (!overlayUpgraded) {
                overlayUpgraded = true
              }

              handlers.processJsonlChunk(data + '\n', streamChatId)
            },
            onOpen: () => {
              if (!overlayUpgraded) overlayUpgraded = isResume
              if (reconnectAttempt > 0) {
                trackReconnect(streamChatId, reconnectAttempt)
              }
              reconnectAttempt = 0
              send({ type: 'RECONNECTED' })
              send({ type: 'HEARTBEAT' })
            },
            onError: (_err) => {
              reconnectAttempt++
              send({ type: 'RECONNECTING' })
            },
            onFatalError: (err) => {
              reject(err)
            },
          },
          abortController.signal,
        )
        streamCloseRef.current = sseConnection.close
      })
    } catch(streamError) {
      if (!isAbortError(streamError)) {
        capturedError = streamError
        throw streamError
      }
    } finally {
      if (!submitStarted) turnAuthority?.releaseUnsubmittedActivity?.()
      // If the stream was aborted (user stop or stale generation), skip full
      // cleanup — handleStopStreaming already handled UI state, and the server
      // may have partially-processed content that shouldn't be fetched yet.
      const wasAborted = abortController.signal.aborted
      const isCurrentAbortController = streamAbortControllerRef.current === abortController
      if (isCurrentAbortController) {
        streamAbortControllerRef.current = undefined
      }
      if (
        activeStreamRef.current?.chatId === streamChatId &&
        activeStreamRef.current.generation === thisGeneration
      ) {
        activeStreamRef.current = undefined
      }

      const isCurrentGeneration = thisGeneration >= latestGenerationRef.current
      const isError = capturedError !== undefined

      if (isCurrentGeneration && !wasAborted) {
        handlers.flushTrailingBuffer(streamChatId)
      }

      // Always cancel pending RAFs regardless of how the stream ended. Flush
      // first for normal current-generation completion so trailing stream
      // events are not dropped by processor cleanup.
      handlers.dispose()

      if (isCurrentGeneration && !wasAborted) {
        if (!isError) {
          onStreamComplete?.(streamChatId)
          trackStreamComplete(streamChatId, false)
        }

        await invalidateChat(streamChatId)
        publishAssistantStreamSyncEvent('chat_stream_settled', streamChatId)

        if (isError) {
          setStreamingMessage(undefined)
        }

        recalculateSpacerAfterStream()
        streamingMessageIdRef.current = undefined
      }

      if (!isCurrentGeneration) {
        // Stale streams must not reset the machine for a newer stream.
      } else if (isError) {
        // Machine enters error state — preserves context (chatId, messageIds)
        // for potential inline retry. Streaming overlay may still be visible
        // with partial content if the server didn't persist it yet.
        send({
          type: 'ERROR',
          error: capturedError instanceof Error ? capturedError : new Error(String(capturedError)),
        })
      } else {
        send({ type: 'STREAM_END' })
      }
    }
  }, [
    accessToken,
    surface,
    userData,
    useNewHotelSearch,
    getSurfaceContextSnapshot,
    latestGenerationRef,
    send,
    setStreamingMessage,
    invalidateChat,
    resetBuffer,
    recalculateSpacerAfterStream,
    onStreamComplete,
    streamingMessageIdRef,
  ])

  // v3: Resume check runs once per chatId on load, then again only when another
  // tab announces a stream start for the same active chat.
  // Uses refs to avoid stale closures and prevent re-firing on every messages change.
  const resumeCheckKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!chatId) {
      resumeCheckKeyRef.current = undefined
      return
    }

    const resumeCheckKey = `${chatId}:${remoteStreamStartVersion}`
    if (resumeCheckKeyRef.current === resumeCheckKey || !messagesReady) return
    resumeCheckKeyRef.current = resumeCheckKey

    if (isStreamingRef.current) return

    const currentMessages = messagesRef.current
    let latestAssistant: MessageItem | undefined
    let latestAssistantIndex = -1
    for (let i = currentMessages.length - 1; i >= 0; i--) {
      if (currentMessages[i].role === 'assistant') {
        latestAssistant = currentMessages[i]
        latestAssistantIndex = i
        break
      }
    }
    if (!latestAssistant) return

    const status = latestAssistant.generationStatus
    if (status !== 'PENDING' && status !== 'IN_PROGRESS') return

    let latestUserMessage: MessageItem | undefined
    for (let i = latestAssistantIndex - 1; i >= 0; i--) {
      if (currentMessages[i].role === 'user') {
        latestUserMessage = currentMessages[i]
        break
      }
    }

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration
    const assistantTurnPolicySnapshot = isAssistantTurnPolicySnapshot(latestAssistant.assistantTurnPolicySnapshot) ?
      latestAssistant.assistantTurnPolicySnapshot :
      undefined

    send({ type: 'RESUME', generation: thisGeneration, chatId, assistantMessageId: latestAssistant.id })

    // Set overlay with PG-checkpointed content (user sees partial immediately)
    setStreamingMessage({
      id: latestAssistant.id,
      parts: latestAssistant.parts,
      role: 'assistant',
      rawContent: latestAssistant.rawContent ?? '',
      messageType: 'message',
      createdAt: latestAssistant.createdAt,
      generationStartedAt: latestAssistant.generationStartedAt,
      generationFinishedAt: latestAssistant.generationFinishedAt,
      ...(assistantTurnPolicySnapshot ? { assistantTurnPolicySnapshot } : {}),
      isFinalized: false,
    })

    const handlers = createHandlers(thisGeneration, latestAssistant.id)

    executeStream(
      {
        kind: 'resume',
        chatId,
        ...(latestUserMessage ? { userMessageId: latestUserMessage.id } : {}),
        assistantMessageId: latestAssistant.id,
        ...(assistantTurnPolicySnapshot ? { assistantTurnPolicySnapshot } : {}),
      },
      thisGeneration,
      handlers,
    ).catch((err) => {
      send({ type: 'ERROR', error: err instanceof Error ? err : new Error(String(err)) })
    })
  }, [
    chatId,
    messagesReady,
    remoteStreamStartVersion,
    createHandlers,
    isStreamingRef,
    messagesRef,
    send,
    setStreamingMessage,
    latestGenerationRef,
    executeStream,
  ])

  // ── Ensure chat exists ──

  const ensureChatId = useCallback(async(message?: string, turnOrderIds?: Array<string>): Promise<number> => {
    if (chatId !== undefined) return chatId

    const createChatOrderIds = unique([...(orderIds ?? []), ...(turnOrderIds ?? [])])
    const chatResponse = await createChat({
      ...(createChatOrderIds.length ? { orderIds: createChatOrderIds } : {}),
    })
    const newChatId = chatResponse.id
    setChatId(newChatId)

    if (isLoggedIn) {
      await refetchChatList()
    }
    if (message) {
      startChatTitleGeneration(newChatId, message, getSurfaceContextSnapshot())
    }

    return newChatId
  }, [chatId, orderIds, setChatId, isLoggedIn, refetchChatList, startChatTitleGeneration, getSurfaceContextSnapshot])

  // ── Submit ──

  const handleSubmit = useCallback(async(message: string, options?: SubmitMessageOptions) => {
    // Universal send choke — every user-initiated send (composer, action
    // buttons via ChatActionsContext.sendMessage, offer-page onSendMessage)
    // funnels through here, so gating here covers all of them. System sends
    // (post-auth follow-up) opt out via bypassSpoofConfirm; regenerate/retry
    // never call this function at all, so they're exempt by construction.
    if (!options?.bypassSpoofConfirm && !(await confirmOrAbort())) return

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    // streamAbortControllerRef tracks protocol lifecycle (set in executeStream,
    // cleared in finally), unlike isStreamingRef which reflects UI-derived state
    // and goes false after content_complete. Must capture before send() — the
    // machine action aborts the old controller and the finally block can clear
    // the ref before this guard runs.
    const hadOpenStream = streamAbortControllerRef.current !== undefined

    send({ type: 'SUBMIT', generation: thisGeneration })

    const userMessageId = uuidV4()
    const userMessageCreatedAt = new Date().toISOString()
    const userMessage: MessageItem = {
      id: userMessageId,
      parts: [{ type: 'text', content: message }],
      role: 'user',
      files: files.map(toChatAttachmentSummary),
      rawContent: message,
      messageType: options?.messageType ?? 'message',
      createdAt: userMessageCreatedAt,
    }

    setPendingUserMessage(userMessage)
    scrollToNewMessage(userMessageId)

    const currentFiles = [...files]
    setFiles([])

    let currentChatId: number | undefined = chatId
    let executeStreamCalled = false
    try {
      currentChatId = await ensureChatId(message, options?.orderIds)
      updateChatLastActivityTimestamp(currentChatId, new Date().toISOString())

      if (hadOpenStream) {
        await invalidateChat(currentChatId)
      }

      const assistantMessageId = uuidV4()
      const handlers = createHandlers(thisGeneration, assistantMessageId)

      // Encode file attachments
      let attachments: Array<ChatMessageAttachment> = []
      if (currentFiles.length > 0) {
        const failedFileNames: Array<string> = []
        const encodedFiles = await Promise.all(
          currentFiles.map(async(file) => {
            try {
              return await fileToChatMessageAttachment(file)
            } catch {
              failedFileNames.push(file.name)
              return undefined
            }
          }),
        )
        attachments = nonNullable(encodedFiles)
        if (failedFileNames.length > 0) {
          showSnackbar(
            intl.formatMessage(
              {
                id: 'aoIXiW41sPchU8OjeWiY',
                defaultMessage: '{count, plural, one {# file} other {# files}} could not be attached: {names}',
                description: 'Warning when one or more files fail to encode for chat attachment',
              },
              { count: failedFileNames.length, names: failedFileNames.join(', ') },
            ),
            'warning',
          )
        }
      }

      executeStreamCalled = true
      await executeStream(
        { kind: 'submit', chatId: currentChatId, content: message, userMessageId, assistantMessageId, attachments },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      // v3 409 chat_busy → leave input populated, show snackbar
      if (isChatBusyResponse(error)) {
        setPendingUserMessage(undefined)
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'wZ2huVGPTp2NwxbvMQ9i',
          defaultMessage: 'A response is already being generated. Please wait.',
          description: 'Warning shown when user tries to send a message while the AI is already generating a response',
        }), 'warning')
        return
      }
      // v3 503 streaming_unavailable → retryable, show snackbar
      if (isStreamingUnavailableResponse(error)) {
        setPendingUserMessage(undefined)
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'TKWVF9LuPHQnCA09kKsX',
          defaultMessage: 'Chat is temporarily unavailable. Please try again.',
          description: 'Error shown when the streaming service is unavailable (503)',
        }), 'critical')
        return
      }
      // v3 413 payload_too_large → not retryable. Attachments go back on the composer so
      // the customer can drop one, unless they attached something else while this was in
      // flight — the composer stays enabled during a send, so never overwrite that.
      if (isPayloadTooLargeResponse(error)) {
        setPendingUserMessage(undefined)
        setStreamingMessage(undefined)
        setFiles(current => current.length > 0 ? current : currentFiles)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'v28F75POI2rG5w42wp-p',
          defaultMessage: 'Your message is too large to send. Remove an attachment and try again.',
          description: 'Error shown when a chat message and its attachments exceed the size the server accepts (413)',
        }), 'critical')
        return
      }

      if (!executeStreamCalled) {
        setPendingUserMessage(undefined)
        setStreamingMessage(undefined)
        send({
          type: 'ERROR',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }

      if (error instanceof FailedStreamTerminalError) {
        trackStreamError(currentChatId, 'unknown')
        return
      }

      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(currentChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'nDA42QsZVeANpf0KNZxr',
          defaultMessage: 'Message failed',
          description: 'Snackbar heading when sending a chat message fails',
        }),
      })
      throw error
    }
  }, [
    confirmOrAbort,
    latestGenerationRef,
    send,
    files,
    setPendingUserMessage,
    scrollToNewMessage,
    setFiles,
    ensureChatId,
    updateChatLastActivityTimestamp,
    createHandlers,
    executeStream,
    setStreamingMessage,
    chatId,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
    invalidateChat,
  ])

  // ── Regenerate ──

  const handleRegenerateResponse = useCallback(async() => {
    const reversed = [...messages].reverse()
    const lastUserMessage = reversed.find((m) => m.role === 'user')
    if (!lastUserMessage) return
    const assistantMessageToRegenerate = reversed.find((m) => m.role === 'assistant')
    if (!assistantMessageToRegenerate) return

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'REGENERATE', generation: thisGeneration })

    // For regeneration, reuse the existing assistant message ID — ID-based
    // deduplication filters out the old server message so the streaming
    // message visually "replaces" it immediately
    const regenerateAssistantId = assistantMessageToRegenerate.id
    const regenerateUserId = lastUserMessage.id

    setStreamingMessage(undefined)

    let currentChatId: number | undefined = chatId
    let executeStreamCalled = false
    try {
      currentChatId = await ensureChatId()

      const handlers = createHandlers(thisGeneration, regenerateAssistantId)

      executeStreamCalled = true
      await executeStream(
        {
          kind: 'submit',
          chatId: currentChatId,
          content: getTextFromParts(lastUserMessage.parts),
          userMessageId: regenerateUserId,
          assistantMessageId: regenerateAssistantId,
          regenerateMessageId: regenerateAssistantId,
        },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      // v3 409 chat_busy → leave input populated, show snackbar
      if (isChatBusyResponse(error)) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'wZ2huVGPTp2NwxbvMQ9i',
          defaultMessage: 'A response is already being generated. Please wait.',
          description: 'Warning shown when user tries to send a message while the AI is already generating a response',
        }), 'warning')
        return
      }
      // v3 503 streaming_unavailable → retryable, show snackbar
      if (isStreamingUnavailableResponse(error)) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'TKWVF9LuPHQnCA09kKsX',
          defaultMessage: 'Chat is temporarily unavailable. Please try again.',
          description: 'Error shown when the streaming service is unavailable (503)',
        }), 'critical')
        return
      }
      // v3 413 payload_too_large → not retryable. Neither path sends files, so only an
      // oversized message body reaches this.
      if (isPayloadTooLargeResponse(error)) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'upGCr27u5N6VVvFUOkGy',
          defaultMessage: 'This message is too large to send.',
          description: 'Error shown when regenerating or retrying a chat message the server rejects as too large (413)',
        }), 'critical')
        return
      }

      if (!executeStreamCalled) {
        setStreamingMessage(undefined)
        send({
          type: 'ERROR',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }

      if (error instanceof FailedStreamTerminalError) {
        trackStreamError(currentChatId, 'unknown')
        return
      }

      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(currentChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'pp-WwVLFlIsUCNNe3QOD',
          defaultMessage: 'Regeneration failed',
          description: 'Snackbar heading when regenerating a chat response fails',
        }),
      })
      throw error
    }
  }, [
    messages,
    latestGenerationRef,
    send,
    setStreamingMessage,
    ensureChatId,
    createHandlers,
    executeStream,
    chatId,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
  ])

  // ── Retry (G4: inline retry with same IDs for server idempotency) ──

  const handleRetry = useCallback(async(config: {
    chatId: number
    userMessageId: string
    assistantMessageId: string
    content: string
    pinnedTurnAuthority?: AssistantOfferPageTurnAuthority
  }) => {
    const { chatId: retryChatId, userMessageId, assistantMessageId, content, pinnedTurnAuthority } = config

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'SUBMIT', generation: thisGeneration })

    const handlers = createHandlers(thisGeneration, assistantMessageId)

    try {
      await executeStream(
        { kind: 'submit', chatId: retryChatId, content, userMessageId, assistantMessageId, pinnedTurnAuthority },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      // v3 409 chat_busy → leave input populated, show snackbar
      if (isChatBusyResponse(error)) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'wZ2huVGPTp2NwxbvMQ9i',
          defaultMessage: 'A response is already being generated. Please wait.',
          description: 'Warning shown when user tries to send a message while the AI is already generating a response',
        }), 'warning')
        return
      }
      // v3 503 streaming_unavailable → retryable, show snackbar
      if (isStreamingUnavailableResponse(error)) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'TKWVF9LuPHQnCA09kKsX',
          defaultMessage: 'Chat is temporarily unavailable. Please try again.',
          description: 'Error shown when the streaming service is unavailable (503)',
        }), 'critical')
        return
      }
      // v3 413 payload_too_large → not retryable. Neither path sends files, so only an
      // oversized message body reaches this.
      if (isPayloadTooLargeResponse(error)) {
        setStreamingMessage(undefined)
        send({ type: 'RESET' })
        showSnackbar(intl.formatMessage({
          id: 'upGCr27u5N6VVvFUOkGy',
          defaultMessage: 'This message is too large to send.',
          description: 'Error shown when regenerating or retrying a chat message the server rejects as too large (413)',
        }), 'critical')
        return
      }

      // executeStream's finally block already handled machine events and cleanup.
      if (error instanceof FailedStreamTerminalError) {
        trackStreamError(retryChatId, 'unknown')
        return
      }

      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(retryChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'nDA42QsZVeANpf0KNZxr',
          defaultMessage: 'Message failed',
          description: 'Snackbar heading when sending a chat message fails',
        }),
      })
      throw error
    }
  }, [
    latestGenerationRef,
    send,
    createHandlers,
    executeStream,
    setStreamingMessage,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
  ])

  // ── Stop ──

  const handleStopStreaming = useCallback(async() => {
    const hadActiveStream = !!streamCloseRef.current || !!streamAbortControllerRef.current || hasCancellableGeneration
    if (!hadActiveStream) return false
    const activeChatId = activeStreamRef.current?.chatId ?? chatId

    // Close SSE first to stop receiving events, then cancel server-side
    streamCloseRef.current?.()
    streamCloseRef.current = undefined
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort()
      streamAbortControllerRef.current = undefined
    }

    if (activeChatId !== undefined) {
      try {
        await cancelMessage(activeChatId, accessToken ?? undefined)
      } catch {
        // Server cancel failed — already closed locally
      }
    }

    currentStreamingGenerationRef.current = 0
    activeStreamRef.current = undefined
    send({ type: 'STOP' })
    streamingMessageIdRef.current = undefined

    if (activeChatId !== undefined) {
      trackStreamComplete(activeChatId, true)

      // Refresh React Query cache so the cancelled message is in server messages.
      // Without this, the next message's overlay replacement causes the cancelled
      // message to vanish (it only existed as a streaming overlay, never fetched).
      // Overlay cleanup happens in useChatMessages once the server message is
      // actually present in rendered query data, avoiding a brief empty frame.
      invalidateChat(activeChatId)
        .catch(() => {})
        .finally(() => {
          publishAssistantStreamSyncEvent('chat_stream_settled', activeChatId)
        })
    }

    return true
  }, [chatId, accessToken, hasCancellableGeneration, send, streamingMessageIdRef, invalidateChat])

  // ── Reset ──

  const resetStreamingState = useCallback(() => {
    streamCloseRef.current?.()
    streamCloseRef.current = undefined
    activeStreamRef.current = undefined
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort()
      streamAbortControllerRef.current = undefined
    }
    streamingMessageIdRef.current = undefined
    send({ type: 'RESET' })
  }, [streamingMessageIdRef, send])

  return { handleSubmit, handleRegenerateResponse, handleStopStreaming, handleRetry, resetStreamingState }
}
